import { useEffect, useMemo } from 'react'
import { useAtom } from 'jotai'
import { useNavigate, Outlet } from 'react-router-dom'
import { Box, Link, Text, VStack, Center } from '@chakra-ui/react'
import { CircularProgressModal } from '@!/components/Modal'
import { CurrentCoinInfo } from '@!/User/common/components'
import { PaymentMainFooter, PaymentTabs, LessCoinsBar } from '@!/User/Payment/components'
import { breadcrumbAtom } from '@!/atoms/breadcrumbAtom'
import { userCoinsAtom, userCoinsIsLoadingAtom, userHashAtom } from '@!/atoms/userInfoAtom'
import { initPaymentAtom, monthlyCoursesAtom, monthlyCourseIdAtom, paymentListAtom, listIndexAtom } from '@!/atoms/paymentAtom'
import { useSearchCard } from '@!/Hooks/useSearchCard'
import { useLessCoinsInfo } from '@!/User/Payment/Hooks'
import { getDevice } from '@!/Util'
import { MonthlyCoursePromoBanner } from '@!/components/Banner'

const PaymentMain = () => {
  const [userCoins] = useAtom(userCoinsAtom)
  const [userCoinsIsLoading] = useAtom(userCoinsIsLoadingAtom)
  const [, initPayment] = useAtom(initPaymentAtom)
  const [, setBreadcrumb] = useAtom(breadcrumbAtom)
  const [userHash] = useAtom(userHashAtom)
  const [searchCardMutation] = useSearchCard(userHash)
  const [monthlyCourses] = useAtom(monthlyCoursesAtom)
  const [, setMonthlyCourseId] = useAtom(monthlyCourseIdAtom)
  const [paymentList] = useAtom(paymentListAtom)
  const [, setListIndex] = useAtom(listIndexAtom)
  const { lessCoins, recommendedCourseName } = useLessCoinsInfo()
  const device = useMemo(() => getDevice(), [])

  const navigate = useNavigate()

  useEffect(() => {
    // 新規カード情報&トークン、選択中のコイン/月額コースのatomをリセット
    initPayment()
    setBreadcrumb([{ name: 'マンガBANGブックス', ref: '/' }, { name: 'コイン購入' }])
  }, [])

  const handleMonthlyCourseSelect = (course) => {
    setMonthlyCourseId(course.id)
    navigate('/monthly_course/select')
  }

  const handleCoinSelect = (index) => {
    if (index >= 0 && index < paymentList.length) {
      setListIndex(index)
      navigate('/select')
    }
  }

  const handleScrollToNotes = (e) => {
    e.preventDefault()
    const el = document.getElementById('coin-point-notes')
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return (
    <Box>
      {userCoinsIsLoading || searchCardMutation.isPending ? (
        <CircularProgressModal isOpen={userCoinsIsLoading || searchCardMutation.isPending} />
      ) : (
        <>
          <Center p={4}>
            <MonthlyCoursePromoBanner />
          </Center>
          <CurrentCoinInfo coinCounts={userCoins} />
          <VStack color="label.secaondary" fontSize="3xs" fontWeight="300" alignItems="flex-start" m={4}>
            <Text>※ マンガBANGブックスでは、マンガBANG（アプリ）で購入したコインはご利用いただけません。</Text>
            <Text>※ マンガBANGブックスでご購入されたコインは、マンガBANG（アプリ）でもご利用頂けます。</Text>
            <Text>
              ※ コインおよびポイントに関する
              <Link as="a" href="#coin-point-notes" onClick={handleScrollToNotes}>
                注意点
              </Link>
              を必ずご確認下さい。
            </Text>
          </VStack>
          <PaymentTabs />
          <Outlet
            context={{
              monthlyCourses,
              paymentList,
              onMonthlyCourseSelect: handleMonthlyCourseSelect,
              onCoinSelect: handleCoinSelect,
            }}
          />
          <PaymentMainFooter />
          {device === 'sp' && lessCoins > 0 && <LessCoinsBar lessCoins={lessCoins} recommendedCourseName={recommendedCourseName} />}
        </>
      )}
    </Box>
  )
}

export default PaymentMain
